import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, ProgressBar, Button, Dropdown } from 'react-bootstrap';
import { Link45deg, InfoCircle, Pen, PlusCircle, Trash, CheckSquare, X, Check } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator/FormGenerator';
import { makeApiRequests } from '../../helpers/api';
import { getObjectForPrefill } from '../../helpers/formHelpers';
import { isAdmin, isSuperAdmin } from '../../helpers/roleHelpers';
import AlertModal from '../AlertModal';
import EntityTable from '../common/EntityTable';
import Loader from '../Loader';
import { ENDPOINTS } from '../../helpers/constants';
import { modelSuggestions } from '../../helpers/modelMatching';
import { modelHeaders, modelAddRows, modelUpdateRows } from './constants';
import { copyToClipboard, normalizeBoxSize } from '../../helpers/global';

const PUBLIC_MHC_HOME_SEARCH_BASE = 'https://mobilehomesvonormy.com/';

const modelTableSearchKeys = ['modelName', 'modelNumber'];

export const getForm = ({ rows, type = 'Add', onSubmitFunctionName }) => {
  const form = {
    forms: [
      {
        name: `${type} Model`,
        markCompulsoryFields: true,
        hideFormName: true,
        compact: true,
        submit: {
          name: type,
          show: true,
          onSubmit: onSubmitFunctionName || (type === 'Add' ? 'onNewModelFormSubmit' : 'onUpdateModelFormSubmit')
        },
        rows
      }
    ]
  };

  return form;
};

// Validate model form data
export const validateModelForm = form => {
  if (!form.modelName && !form.modelNumber) {
    toast.error('Either Model Name or Model Number must be provided');
    return false;
  }

  const normalizedBox = normalizeBoxSize(form.boxSize);
  if (!normalizedBox) {
    toast.error('Box Size must be in the format a x b (e.g., 1x3)');
    return false;
  }
  form.boxSize = normalizedBox;

  const virtualTourVideos = form.virtualTourVideo;
  if (Array.isArray(virtualTourVideos)) {
    if (virtualTourVideos.some(v => v && ['WAITING_FOR_UPLOAD', 'UPLOADING'].includes(v.status))) {
      toast.error('Wait for the virtual tour video to finish uploading before saving.');
      return false;
    }
    if (virtualTourVideos.some(v => v && v.status === 'ERROR')) {
      toast.error('Video upload failed. Remove the video or choose another file.');
      return false;
    }
  }

  return true;
};

// function to convert dot notation fields to nested values in paymentRange object
const convertPaymentRangeToNested = (form) => {
  if (form['paymentRange.start'] !== undefined || form['paymentRange.end'] !== undefined) {
    form.paymentRange = {
      start: form['paymentRange.start'] || null,
      end: form['paymentRange.end'] || null
    };
    delete form['paymentRange.start'];
    delete form['paymentRange.end'];
  }
  return form;
};

/** Strip images, normalize virtual tour (API expects string | null, not []). */
export const prepareModelFormForApi = (form) => {
  const photos =
    (form.images || [])
      .filter(img => img.filename)
      .map(img => ({
        url: img.filename,
        uploadedAt: img.uploadedAt || new Date()
      })) || [];

  const videoData = form.virtualTourVideo;
  let virtualTourVideo = null;
  if (videoData && Array.isArray(videoData) && videoData.length > 0) {
    const uploadedVideo = videoData.find(v => v.status === 'UPLOADED' && v.filename);
    const safeUrl = typeof videoData?.[0]?.url === 'string' && !videoData[0].url.startsWith('blob:') ? videoData[0].url: null;
    virtualTourVideo = uploadedVideo?.filename || safeUrl;
  }

  const { images, virtualTourVideo: _, ...cleanForm } = form;
  convertPaymentRangeToNested(cleanForm);

  cleanForm.virtualTourVideo = virtualTourVideo || null;
  cleanForm.virtualTourLink = cleanForm.virtualTourLink?.trim() || null;

  return { photos, cleanForm };
};

export const AddEditModal = ({
  show,
  toBeEditedModel,
  onHide,
  showProgress,
  addForm,
  updateForm,
  defaultValues,
  disclaimer,
  existingModels = [],
  onSelectExisting
}) => {
  const [draftIdentity, setDraftIdentity] = useState({});
  useEffect(() => { setDraftIdentity(defaultValues || {}); }, [show, defaultValues?.modelName, defaultValues?.modelNumber]);
  const suggestions = useMemo(() => toBeEditedModel ? [] : modelSuggestions(existingModels, draftIdentity), [existingModels, draftIdentity, toBeEditedModel]);
  const formValues = useMemo(() => {
    if (toBeEditedModel) {
      const videoValue = toBeEditedModel.virtualTourVideo
        ? [{
          url: toBeEditedModel.virtualTourVideo,
          filename: toBeEditedModel.virtualTourVideo,
          status: 'UPLOADED',
          id: 'existing-video',
          isExisting: true
        }]
        : [];

      const prefill = getObjectForPrefill(toBeEditedModel);
      return {
        'Update Model': {
          ...prefill,
          images: (toBeEditedModel.photos || []).map((photo, index) => ({
            ...photo,
            id: `existing-${index}`,
            filename: photo.url,
            isExisting: true
          })),
          virtualTour: {
            linkPreValue: toBeEditedModel.virtualTourLink || '',
            videoPreValue: videoValue
          }
        }
      };
    }

    if (defaultValues) {
      return {
        'Add Model': defaultValues
      };
    }

    return {};
  }, [toBeEditedModel, defaultValues]);

  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title>
          <h6 className="mb-0">{toBeEditedModel ? 'Update Model' : 'Add New Model'}</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto px-1" onChangeCapture={event => {
        const id = String(event.target.id || '').toLowerCase();
        const key = id.includes('modelname') ? 'modelName' : id.includes('modelnumber') ? 'modelNumber' : null;
        if (key) { const value = event.target.value; setDraftIdentity(current => ({ ...current, [key]: value })); }
      }}>
        <p className="px-3 small text-muted">Check the full model name and number, including the final digits. Matching records are reused; older models remain on file. A different model number requires a new record.</p>
        {suggestions.length > 0 && <Alert variant={suggestions.some(row => row.exact) ? 'warning' : 'info'} className="mx-3">
          <b>Check existing models</b><div className="small">An exact name or number is reused. Similar numbers may be different versions—compare the entire identifier.</div>
          {suggestions.map(({model, exact}) => <div key={model._id} className="mt-1 d-flex align-items-center justify-content-between">
            <span>{[model.modelName, model.modelNumber].filter(Boolean).join(' — ')} {exact ? '(matching identifier)' : '(similar)'}{model.enable === false ? ' · disabled' : ''}</span>
            {onSelectExisting && <Button size="sm" variant="outline-primary" disabled={showProgress} onClick={() => onSelectExisting(model)}>Use this model</Button>}
          </div>)}
        </Alert>}
        <FormGenerator
          prefix={toBeEditedModel ? 'update-model-form' : 'add-model-form'}
          formJson={toBeEditedModel ? { ...updateForm, forms: updateForm.forms.map(form => ({ ...form, rows: form.rows.map(row => ({ ...row, columns: row.columns.map(column => ({ ...column, field: { ...column.field, disabled: ['modelName', 'modelNumber'].includes(column.field.id) || column.field.disabled } })) })) })) } : addForm}
          formValues={formValues}
        />
        {disclaimer && (
          <div className="px-3">
            <Alert
              variant="warning"
              className="mb-3 d-flex align-items-center justify-content-center  text-black text-center py-1 px-2 rounded-lg"
            >
              <InfoCircle className="mr-2" size={20} style={{ flexShrink: 0 }} />
              <span className="mb-0 text-center">{disclaimer}</span>
            </Alert>
          </div>
        )}
        {showProgress && (
          <ProgressBar now={100} animated striped label={toBeEditedModel ? `Updating Model...` : `Saving Model....`} />
        )}
      </Modal.Body>
    </Modal>
  );
};

const ManufacturerModels = ({ manufacturer, refetchChoices }) => {
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [toBeDeletedModel, setToBeDeletedModel] = useState(null);
  const [modelActionMeta, setModelActionMeta] = useState(null);
  const [modelAddingUpdating, setModelAddingUpdating] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(null);
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);

  const fetchModels = async () => {
    setLoading(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_BY_MANUFACTURER(manufacturer?._id),
      method: 'GET'
    });

    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setModels(response.results || response);
  };

  useEffect(() => {
    fetchModels();
  }, []);

  const deleteModel = async () => {
    setDeleteInProgress(true);
    const { error } = await makeApiRequests({ endpoint: ENDPOINTS.MODELS_WITH_ID(toBeDeletedModel._id), method: 'PUT', requestBody: { enable: false } });
    setDeleteInProgress(false);
    if (error) { toast.error(error); return; }
    setModels(models.map(model => model._id === toBeDeletedModel._id ? { ...model, enable: false } : model));
    setToBeDeletedModel(null);
    if (refetchChoices) await refetchChoices();
  };

  const onNewModelFormSubmit = async form => {
    setModelAddingUpdating(true);

    if (!validateModelForm(form)) {
      setModelAddingUpdating(false);
      return false;
    }

    const { photos, cleanForm } = prepareModelFormForApi(form);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_BASE,
      requestBody: { ...cleanForm, photos, manufacturer: manufacturer?._id }
    });

    setModelAddingUpdating(false);
    if (error) {
      toast.error(error);
      return false;
    }

    setModels([response, ...models.filter(model => model._id !== response._id)]);
    if (response.reusedExisting) toast.info('This model already exists. Its saved details were preserved.');
    setModelActionMeta(null);

    if (refetchChoices) {
      await refetchChoices();
    }
    return true;
  };

  const onUpdateModelFormSubmit = async form => {
    setModelAddingUpdating(true);

    if (!validateModelForm(form)) {
      setModelAddingUpdating(false);
      return false;
    }

    const { photos, cleanForm } = prepareModelFormForApi(form);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_WITH_ID(modelActionMeta.model._id),
      requestBody: { ...cleanForm, photos },
      method: 'PUT'
    });

    setModelAddingUpdating(false);
    if (error) {
      toast.error(error);
      return false;
    }

    const modelIndex = models.findIndex(i => i._id === response._id);
    models[modelIndex] = { ...models[modelIndex], ...response };
    setModels([...models]);
    setModelActionMeta(null);

    if (refetchChoices) {
      await refetchChoices();
    }
    return true;
  };

  const handleBulkUpdate = async (enable) => {
    if (selectedModels.length === 0) {
      toast.error('Please select at least one model');
      return;
    }

    setShowBulkConfirmation({ enable, count: selectedModels.length });
  };

  const confirmBulkUpdate = async () => {
    const { enable } = showBulkConfirmation;
    setBulkActionInProgress(true);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.MODELS_BULK_UPDATE,
      requestBody: {
        modelIds: selectedModels,
        updateData: { enable }
      }
    });

    setBulkActionInProgress(false);
    setShowBulkConfirmation(null);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success(`Successfully ${enable ? 'enabled' : 'disabled'} ${response.modifiedCount} model(s)`);
    const updatedModels = models.map(model =>
      selectedModels.includes(model._id) ? { ...model, enable } : model
    );
    setModels(updatedModels);
    setSelectedModels([]);

    if (refetchChoices) {
      await refetchChoices();
    }
  };

  const toggleModelSelection = (modelId) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(id => id !== modelId)
        : [...prev, modelId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedModels.length === models.length) {
      setSelectedModels([]);
    } else {
      setSelectedModels(models.map(m => m._id));
    }
  };

  const copyEnabledModelPublicLink = model => {
    if (!model.enable) return;
    const id = model._id;
    if (!id) {
      toast.error('Model id is missing; cannot build the link.');
      return;
    }
    const url = `${PUBLIC_MHC_HOME_SEARCH_BASE}homes/${encodeURIComponent(String(id))}/`;
    copyToClipboard(url)
      .then(() => toast.success('Model link copied!'))
      .catch(() => toast.error('Could not copy link.'));
  };

  window['onNewModelFormSubmit'] = onNewModelFormSubmit;
  window['onUpdateModelFormSubmit'] = onUpdateModelFormSubmit;

  return (
    <div fluid className="h-100 py-3 px-md-3 border border-primary bg-white">
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <h6 className="mb-0 mr-2">
            {manufacturer?.name} Models
          </h6>

          {isSuperAdmin() && selectedModels.length > 0 && (
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="success" className="d-flex align-items-center">
                <CheckSquare className="mr-1" size={14} />
                <span className="mb-0">Enable All ({selectedModels.length})</span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="p-1">
                <Dropdown.Item
                  as="button"
                  className="text-success d-flex align-items-center overflow-hidden"
                  onClick={() => handleBulkUpdate(true)}
                >
                  <Check className="mr-2" size={16} fill="green" />
                  <span className="mb-0">Yes</span>
                </Dropdown.Item>

                <Dropdown.Item
                  as="button"
                  className="text-danger d-flex align-items-center overflow-hidden"
                  onClick={() => handleBulkUpdate(false)}
                >
                  <X className="mr-2" size={16} fill="red" />
                  <span className="mb-0">No</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>

        <div>
          {isAdmin() && (
            <Button
              size="sm"
              variant="success"
              onClick={() => setModelActionMeta({ mode: 'add' })}
            >
              <PlusCircle className="mr-1" />
              New Model
            </Button>
          )}
        </div>
      </div>


      <hr className="my-1" />
      {loading ? (
        <Loader />
      ) : (
        <>
          <EntityTable
            rowKeyField={'_id'}
            data={models}
            quickSearch={false}
            searchQueryKeys={modelTableSearchKeys}
            manufacturer={manufacturer}
            headers={modelHeaders}
            showCheckboxes={isSuperAdmin()}
            selectedRows={selectedModels}
            onRowSelect={toggleModelSelection}
            onSelectAll={toggleSelectAll}
            actionCell={model => (
              <div>
                {isAdmin() && (
                  <>
                  {model.enable === true && (
                      <Button
                        variant="outline-primary"
                        className="px-1 py-0 mr-1"
                        title="Copy public home URL (enabled models only)"
                        onClick={() => copyEnabledModelPublicLink(model)}
                      >
                        <Link45deg size={12} />
                      </Button>
                    )}
                    <Button
                      variant="outline-primary"
                      className="px-1 py-0 mr-1"
                      onClick={() => setModelActionMeta({ model, mode: 'edit' })}
                    >
                      <Pen size={10} />
                    </Button>                  
                    <Button variant="outline-danger" className="px-1 py-0 " title="Hide model and preserve history" disabled={model.enable === false} onClick={() => setToBeDeletedModel(model)}>
                      <X size={12} />
                    </Button>
                  </>
                )}
              </div>
            )}
          />
          {models.length > 0 && (
            <AlertModal
              show={toBeDeletedModel !== null}
              alertText={`Hide this model? Its record, model number and linked inventory will be preserved.`}
              onHide={() => setToBeDeletedModel(null)}
              onDismissClick={() => setToBeDeletedModel(null)}
              onContinueClick={deleteModel}
              progressText={`Hiding model...`}
              showProgress={deleteInProgress}
            />
          )}
          <AlertModal
            show={showBulkConfirmation !== null}
            alertText={`Are you sure you want to ${showBulkConfirmation?.enable ? 'enable' : 'disable'} ${showBulkConfirmation?.count} model(s)?`}
            onHide={() => setShowBulkConfirmation(null)}
            onDismissClick={() => setShowBulkConfirmation(null)}
            onContinueClick={confirmBulkUpdate}
            progressText={`${showBulkConfirmation?.enable ? 'Enabling' : 'Disabling'} models...`}
            showProgress={bulkActionInProgress}
          />
          <AddEditModal
            existingModels={models}
            show={modelActionMeta !== null}
            onHide={() => setModelActionMeta(null)}
            toBeEditedModel={modelActionMeta ? modelActionMeta.model : null}
            showProgress={modelAddingUpdating}
            addForm={getForm({ rows: modelAddRows, type: 'Add' })}
            updateForm={getForm({ rows: modelUpdateRows, type: 'Update' })}
          />
        </>
      )}
    </div>
  );
};

export default ManufacturerModels;
